import {
  getFirstDateOfWeek,
  getLastDateOfWeek,
} from "@/features/calendar/utils/dateUtils";
import { sortCalendarItems } from "@/features/calendar/utils/sortItems";
import { isDropinTask } from "@/features/tasks/isDropinTask";
import {
  type Appointment,
  type Task,
  useCalendarAppointmentsSearch,
  useTasks,
  useTeam,
} from "@repo/api";
import { hasItems } from "@repo/utils";
import { endOfDay, isBefore, startOfDay, subDays } from "date-fns";

import { type CalenderAppointmentsSearchFilters } from "../calendarFilter/appointmentFilterReducer";
import {
  selectEmail,
  selectTeam,
} from "../calendarFilter/appointmentFilterSelector";
import { getAppointments } from "../utils";

export const useCalendarAppointments = (
  shouldSearch: boolean,
  filters: CalenderAppointmentsSearchFilters,
  refresh?: boolean,
  shouldFetchOpenSlots?: boolean
) => {
  const appointmentDate: [Date, Date] =
    filters.tableView === "OVERVIEW"
      ? [
          getFirstDateOfWeek(
            filters.selectedDate,
            filters.calendarSettings.firstDayOfWeek
          ),
          getLastDateOfWeek(
            filters.selectedDate,
            filters.calendarSettings.firstDayOfWeek
          ),
        ]
      : [startOfDay(filters.selectedDate), endOfDay(filters.selectedDate)];
  const searchFilters = {
    ...filters,
    appointmentDate,
    emailId: selectEmail(filters),
    teamId: selectTeam(filters),
  };

  const isSearchingBeforeAllowedDate = isBefore(
    appointmentDate[0],
    subDays(startOfDay(new Date()), 60)
  );

  const shouldSearchTasks =
    shouldSearch &&
    searchFilters !== undefined &&
    (hasItems(searchFilters.emailId) || searchFilters.teamId !== undefined);

  const shouldSearchAppointments =
    shouldSearchTasks && !isSearchingBeforeAllowedDate;

  const { data: tasks = [], isLoading: isLoadingTasks } = useTasks(
    shouldSearchTasks
      ? {
          fromDate: appointmentDate[0],
          toDate: appointmentDate[1],
          emails: searchFilters.emailId,
          teamId: searchFilters.teamId,
          assignedToTeam:
            searchFilters.teamCalendar.selection?.assignedToTeam ?? false,
        }
      : undefined
  );

  const {
    data,
    isLoading: isLoadingAppointments,
    error: appointmentsError,
    size,
    setSize,
    mutate: mutateSearchAppointments,
  } = useCalendarAppointmentsSearch(
    shouldSearchAppointments ? searchFilters : undefined,
    refresh ?? true
  );

  // Open Slot appointments: same endpoint as the main calendar with an extra
  // `openSlotUnit` parameter so the backend returns only unassigned open slots
  // for the selected team's business unit. Intended for the TEAM view only —
  // `selectTeam` returns `undefined` for other views, in which case the fetch
  // is skipped. Fetched once with no auto-revalidation (advisory list; caller
  // uses `mutateOpenSlots` for manual refresh).
  const openSlotsTeamId = selectTeam(filters);
  const { data: selectedTeam } = useTeam(openSlotsTeamId);
  const openSlotUnit = selectedTeam?.buCode;
  const shouldFetchOpenSlotsResolved =
    Boolean(shouldFetchOpenSlots) &&
    openSlotsTeamId !== undefined &&
    openSlotUnit !== undefined;

  const {
    data: openSlotData,
    isLoading: isLoadingOpenSlotsRaw,
    error: openSlotsError,
    mutate: mutateOpenSlots,
  } = useCalendarAppointmentsSearch(
    shouldFetchOpenSlotsResolved
      ? {
          appointmentDate,
          teamId: openSlotsTeamId,
          openSlotUnit,
        }
      : undefined,
    false,
    {
      refreshInterval: 0,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
    }
  );
  const openSlotItems = getAppointments(openSlotData);

  const appointments = getAppointments(data).map((appointment) =>
    appointment.assignedTeam === undefined &&
    !appointment.assignedCoworker &&
    searchFilters.teamId !== undefined
      ? { ...appointment, assignedTeam: searchFilters.teamId }
      : appointment
  );

  const dropInAppointments =
    appointments?.filter((t) => t.type === "DROP_IN") ?? [];
  const bokaAppointments =
    appointments?.filter((t) => t.type === "BOKA_APPOINTMENT") ?? [];

  const applyFiltersForTasks = (items: Task[]) => {
    let filteredItems = items;

    if (filters.calendarFilters.hideCompletedEvents) {
      filteredItems = filteredItems.filter(
        (item) => !item.completed?.taskCompletedDateTime
      );
    }

    if (filters.calendarFilters.filteredTasks.length === 0) {
      return filteredItems;
    }

    return filteredItems.filter((item) => {
      return filters.calendarFilters.filteredTasks.some((filter) => {
        const [parentFilter, subFilter] = filter.split("_");
        return (
          item.parentTaskType === parentFilter && item.subTaskType === subFilter
        );
      });
    });
  };

  const applyFiltersForDropInAppointments = (items: Appointment[]) => {
    if (filters.calendarFilters.filteredTasks.length === 0) {
      return items;
    }
    if (filters.calendarFilters.filteredTasks.includes("appointment_dropIn")) {
      return items.filter((item) => {
        return item.type === "DROP_IN";
      });
    } else {
      return [];
    }
  };

  const applyFiltersForBokaAppointments = (items: Appointment[]) => {
    if (filters.calendarFilters.filteredTasks.length === 0) {
      return items;
    }
    if (filters.calendarFilters.filteredTasks.includes("appointment_BOKA")) {
      return items.filter((item) => {
        return item.type === "BOKA_APPOINTMENT";
      });
    } else {
      return [];
    }
  };

  const filteredTasks = applyFiltersForTasks(tasks);
  const filteredDropInAppointments =
    applyFiltersForDropInAppointments(dropInAppointments);
  const filteredBokaAppointments =
    applyFiltersForBokaAppointments(bokaAppointments);

  const filterItems = () => {
    let items = [
      ...filteredBokaAppointments,
      ...filteredDropInAppointments,
      ...filteredTasks.filter(
        (t) =>
          !isDropinTask(t.parentTaskType, t.subTaskType) &&
          // TODO: This should be removed when open slots are removed
          t.parentTaskType !== "boka"
      ),
    ].sort(sortCalendarItems);

    if (filters.calendarFilters.hideCanceledEvents) {
      items = items.filter((item) => {
        if (
          item.appointmentStatus === "CANCELLED" ||
          item.appointmentStatus === "CANCELLED_NO_SHOW"
        ) {
          return false;
        }

        return true;
      });
    }

    if (filters.calendarFilters.hideCompletedEvents) {
      items = items.filter((item) => {
        if (item.appointmentStatus === "COMPLETED") {
          return false;
        }

        return true;
      });
    }

    return items;
  };

  const items = filterItems();
  const totalSize = items.length;

  return {
    items,
    isLoadingTasks,
    isLoadingAppointments: isLoadingAppointments && !appointmentsError,
    isSearchingBeforeAllowedDate,
    size,
    totalSize,
    setSize,
    mutateSearchAppointments,
    openSlotItems,
    isLoadingOpenSlots: isLoadingOpenSlotsRaw && !openSlotsError,
    mutateOpenSlots,
  };
};
